import { inject, injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import { IAnalyticsGatewayToken, type IAnalyticsGateway } from '@/shared/analytics/AnalyticsGateway';
import type { AnalyticsField, AnalyticsFilter, AnalyticsMetric } from '@/shared/analytics/AnalyticsTypes';

const BREAKDOWN_METRICS: AnalyticsMetric[] = ['turns', 'sessions', 'totalTokens', 'inputTokens', 'outputTokens', 'allocatedCostUsd'];
const BREAKDOWN_ROW_LIMIT = 200;

export interface DrillBreakdownRow {
    /** The drill-field value (e.g. the person's user id). */
    key: string;
    department: string;
    turns: number;
    sessions: number;
    totalTokens: number;
    inputTokens: number;
    outputTokens: number;
    /** Allocated real spend ($); 0 when no cost data covers the row. */
    allocatedCostUsd: number;
}

/**
 * The intermediate drill level between a topic slice and its prompt evidence:
 * a ranked breakdown of the slice by the topic's `drillField` (e.g. the people
 * behind a use case), ordered by activity.
 */
@injectable()
export class SurfaceDrillBreakdownViewModel extends BaseViewModel {
    rows: DrillBreakdownRow[] = [];
    loading = false;
    errorMessage: string | null = null;

    constructor(@inject(IAnalyticsGatewayToken) private gateway: IAnalyticsGateway) {
        super();
    }

    async load(drillField: AnalyticsField, filters: AnalyticsFilter[]): Promise<void> {
        this.rows = [];
        this.loading = true;
        this.errorMessage = null;
        this.notifyChange();
        try {
            const result = await this.gateway.aggregate({
                source: 'interactions',
                groupBy: [drillField, 'user_department'],
                metrics: BREAKDOWN_METRICS,
                orderBy: { metric: 'turns', direction: 'desc' },
                limit: BREAKDOWN_ROW_LIMIT,
                filters,
            });
            this.rows = result.rows.map((row) => ({
                key: row.keys[drillField] ?? '—',
                department: row.keys['user_department'] ?? '—',
                turns: row.metrics.turns,
                sessions: row.metrics.sessions,
                totalTokens: row.metrics.totalTokens,
                inputTokens: row.metrics.inputTokens,
                outputTokens: row.metrics.outputTokens,
                allocatedCostUsd: row.metrics.allocatedCostUsd,
            }));
        } catch (err) {
            this.errorMessage = err instanceof Error ? err.message : String(err);
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }
}
